/* eslint-disable react-compiler/react-compiler */
/* eslint-disable react-hooks/exhaustive-deps */
import { ArrowDownward, ArrowUpward } from "@mui/icons-material";
import {
  Grid,
  Paper,
  Step,
  StepContent,
  StepLabel,
  Stepper,
} from "@mui/material";
import { Box } from "@mui/system";
import { Alert, Button, Typography } from "@platform/design-system";
import isEqual from "lodash/isEqual";
import { useEffect, useState } from "react";
import { useNavigate } from "react-router";
import { useApi } from "@platform/api";
import { CURRENT_COVERAGES } from "@platform/rfp-core";

import { submitEvent } from "../../utils/tracking";
import { Loader } from "../../components";
import AddCurrentCoverages from "../../components/addCoverages/addCurrentCoverages";
import NewClientForm from "../../components/clientFormNew";
import { ROUTE_NAMES } from "../../constants";
import {
  CLIENT_FORM,
  CLIENT_REDUCER_ACTIONS,
  CLIENT_TYPES,
  IS_CLIENT_FORM_VALID,
} from "../../constants/client";
import { formatDate, getClientPayload, extractErrorMessage } from "../../utils";
import { ClientBtnSection, GradientBox } from "../RFP/components";

import ClientTiles from "./clientTiles";

const { UPDATE_CLIENT_FIELD } = CLIENT_REDUCER_ACTIONS;
const {
  CLIENT_TYPE,
  CLIENT_ID,
  NAME,
  CLIENT_SINCE,
  STATE,
  SITUS_STATE,
  BROKER_TYPE,
  BROKER_HAS_MULTIPLE_STRATEGIC_TIERS,
} = CLIENT_FORM;

const ClientNew = ({
  data: {
    states,
    brokerClientInfoConfig,
    brokerClientInfoAvailableOptions,
    oneDigitalOfficeLocations,
  },
  client,
  setClient,
  loading,
  error,
}) => {
  const [activeStep, setActiveStep] = useState(0);
  const [coverages, setCoverages] = useState([]);
  const [clientLoading, setClientLoading] = useState(false);
  const [clientError, setClientError] = useState(false);
  const [originalClient, setOriginalClient] = useState(null);
  const navigate = useNavigate();
  const axiosInstance = useApi();
  const clientId = client[CLIENT_ID];
  const clientBrokerType = client[BROKER_TYPE];
  const brokerHasMultipleStrategicTiers =
    client[BROKER_HAS_MULTIPLE_STRATEGIC_TIERS];
  const isProspect = client[CLIENT_TYPE] === CLIENT_TYPES.PROSPECT_CLIENT;

  useEffect(() => {
    setActiveStep(0);
    setCoverages([]);
  }, [client[CLIENT_TYPE]]);

  const handleNextClientInfo = async () => {
    if (!client[IS_CLIENT_FORM_VALID]) return;

    const initPayload = getClientPayload(
      {
        ...client,
        [STATE]: client[STATE].abbreviation,
        [SITUS_STATE]: client[SITUS_STATE]?.abbreviation,
        clientSince: !isProspect ? formatDate(client[CLIENT_SINCE]) : null,
      },
      brokerClientInfoConfig,
    );

    if (isEqual(initPayload, originalClient)) {
      setActiveStep(1);
      return;
    }

    let isSuccess = false;
    try {
      setClientError(null);
      setClientLoading(true);
      const payload = {
        ...initPayload,
        isProspect,
      };
      if (clientId) {
        await axiosInstance.patch(
          `/brokerportal/v1/clients/${clientId}`,
          payload,
        );
        submitEvent("Client Created", {
          client_name: client.name,
          client_size: client.totalBenefitsEligibleEmployees,
          client_type: "current",
        });
      } else {
        const { data } = await axiosInstance.post(
          "/brokerportal/v1/clients",
          payload,
        );
        setClient({
          type: UPDATE_CLIENT_FIELD,
          name: CLIENT_ID,
          value: data.id,
        });
        setClient({
          type: UPDATE_CLIENT_FIELD,
          name: BROKER_TYPE,
          value: data.brokerType,
        });
        const type = payload.isProspect ? "prospect" : "new";

        submitEvent("Client Created", {
          client_name: client.name,
          client_size: client.totalBenefitsEligibleEmployees,
          client_type: type,
        });
      }
      setOriginalClient(initPayload);
      isSuccess = true;
    } catch (err) {
      setClientError(extractErrorMessage(err));
    } finally {
      setClientLoading(false);
    }
    if (isSuccess) {
      setActiveStep(1);
    }
  };

  function handleAddCoverages(newCoverages) {
    setCoverages((old) => [...old, ...newCoverages]);
  }

  const handleChange = ({ target }) => {
    const { value, name } = target;
    setClient({
      type: UPDATE_CLIENT_FIELD,
      name,
      value,
    });
  };

  const clientFormStep = () => (
    <>
      {clientLoading ? (
        <Loader mt={2} isCentered={false} sx={{ textAlign: "center" }} />
      ) : (
        <>
          <NewClientForm
            client={client}
            states={states}
            onChange={handleChange}
            isProspect={isProspect}
            brokerClientInfoConfig={brokerClientInfoConfig}
            brokerClientInfoAvailableOptions={brokerClientInfoAvailableOptions}
            oneDigitalOfficeLocations={oneDigitalOfficeLocations}
          />
          <Button
            variant="secondary"
            startIcon={<ArrowDownward />}
            onClick={handleNextClientInfo}
            disabled={!client[IS_CLIENT_FORM_VALID]}
            sx={{ ml: 3 }}
          >
            submit & next
          </Button>
        </>
      )}
    </>
  );

  const addCoveragesStep = () => (
    <>
      {clientLoading ? (
        <Box position="relative" my={2}>
          <Loader />
        </Box>
      ) : (
        <AddCurrentCoverages
          brokerType={clientBrokerType}
          brokerHasMultipleStrategicTiers={brokerHasMultipleStrategicTiers}
          coverages={coverages}
          onAddCoverages={handleAddCoverages}
          clientId={clientId}
          hideNonNACols
          showConnectedCarriers={!isProspect}
          isAdmin={false}
          embeded
          showTitle={false}
          clientType={client[CLIENT_TYPE]}
          client={client}
          isConsultant={false}
        />
      )}
      <Button
        variant="secondary"
        startIcon={<ArrowUpward />}
        onClick={() => setActiveStep(0)}
        disabled={clientLoading}
      >
        back to client information
      </Button>
    </>
  );

  const steps = [
    {
      key: "Client Information",
      label: (
        <Typography variant={activeStep === 0 ? "header-large" : "body-large"}>
          {activeStep === 0 ? (
            "Client Information"
          ) : (
            <strong>Client Information</strong>
          )}
        </Typography>
      ),
      component: () => clientFormStep(),
    },
    {
      key: "Current Coverages",
      label: (
        <Typography variant={activeStep === 1 ? "header-large" : "body-large"}>
          {activeStep === 1 ? (
            "Current Coverages"
          ) : (
            <strong>Current Coverages</strong>
          )}
        </Typography>
      ),
      component: () => addCoveragesStep(),
    },
  ];

  const defaultClientName = isProspect ? "Prospect Client" : "New Client";
  const clientName = client[NAME] || defaultClientName;

  return (
    <ClientTiles client={client} setClient={setClient}>
      {loading && (
        <Loader mt={2} isCentered={false} sx={{ textAlign: "center" }} />
      )}
      {(error || clientError) && (
        <Box mb={2} width="inherit">
          <Alert severity="error">
            {extractErrorMessage(error || clientError)}
          </Alert>
        </Box>
      )}
      {states && (
        <Grid component={Paper} p={5} mt={10}>
          <Typography variant="display-small">{clientName}</Typography>
          <GradientBox mr="auto" />
          <Stepper activeStep={activeStep} orientation="vertical">
            {steps.map((step) => (
              <Step key={step.key}>
                <StepLabel>{step.label}</StepLabel>
                <StepContent>{step.component()}</StepContent>
              </Step>
            ))}
          </Stepper>
        </Grid>
      )}
      <ClientBtnSection
        mainBtn="NEXT: RFP DETAILS"
        mainDisabled={!client[IS_CLIENT_FORM_VALID] || activeStep === 0}
        mainAction={() =>
          navigate(`/${ROUTE_NAMES.RFP}`, {
            state: {
              client,
              prevPath: location.pathname,
              [CURRENT_COVERAGES]: coverages,
            },
          })
        }
        secBtn="CANCEL"
        secVariant="outlined"
        secHasIcon={false}
        secAction={() => navigate(`/${ROUTE_NAMES.RFPS}`)}
      />
    </ClientTiles>
  );
};

export default ClientNew;
